import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import translationEn from "./locales/en/translation";
import translationUa from "./locales/ua/translation";
import { AVAILABLE_LOCALES, PUBLIC_LOCALE_STORAGE_KEY } from './constants'
import moment from 'moment';
import 'moment/locale/uk';
import { momentLocale } from 'utils';
import { setNameLanguage } from 'utils/Athlete/personName';

/*
 * WHICH LANGUAGE A PAGE OPENS IN — one rule for the platform and the public
 * pages (organiser, 30 Oct 2026: «UI береться із налаштувань локалі, якщо
 * українська мова є, то використовуємо українську, якщо не зрозуміло яка
 * локаль, чи локаль не підтримується поки нашою системою, то англійська»):
 *
 *  1. the language chosen here before — it survives a reload (18 Oct 2026:
 *     «якщо я обрав англійську мову … і перевантажив сторінку, скидається
 *     вибір мови»);
 *  2. else the browser's: Ukrainian among its languages → Ukrainian;
 *  3. anything else — another language, none, unreadable → English.
 *
 * The detector WRITES the language to localStorage (`i18nextLng`) on every
 * change, so a platform visitor's first language is kept from then on.
 */
export const LOCALE_STORAGE_KEY = 'i18nextLng';

/** Ukrainian among the browser's languages → `ua`; otherwise `en`. */
export const browserLocale = languages => ((languages || []).some(language => /^uk\b/i.test(language || '')) ? 'ua' : 'en');

const browserLanguages = () => {
    const { navigator } = window;

    return navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
};

/** A locale this platform has, or null. */
const known = locale => (AVAILABLE_LOCALES.some(available => available.locale === locale) ? locale : null);

const readKnown = key => {
    try {
        return known(window.localStorage.getItem(key));
    } catch (e) {
        // Storage blocked (a private window, a policy): the browser's, every time.
        return null;
    }
};

export const storedLocale = () => readKnown(LOCALE_STORAGE_KEY) || browserLocale(browserLanguages());

/*
 * THE PUBLIC PAGES (30 Oct 2026: «можуть бути міжнародні змагання… якщо буде
 * іноземець, то щоб йому показувало EN версію розкладу, та його монітору»).
 * The participants' monitor (/m/…) and the published schedule (/s/…) are
 * opened by anybody from a QR code; the same rule, but the visitor's choice on
 * the page (PublicLanguageToggle) is kept under its own key, so it never
 * changes the platform's language for a secretary on the same computer — nor
 * the platform's choice the public page's.
 *
 * A SCREEN (/display/…) is one too since 1 Nov 2026: the chief secretary sets
 * its language from the floor, and a laptop opening a screen's URL must not
 * have the platform's language changed by it.
 */
const PUBLIC_PATH = /^\/(m|s|display)\//;

export const isPublicPath = pathname => PUBLIC_PATH.test(pathname || '');

export const publicLocale = () => readKnown(PUBLIC_LOCALE_STORAGE_KEY) || browserLocale(browserLanguages());

const onPublicPage = isPublicPath(window.location.pathname);

i18n
    .use(LanguageDetector)
    .on('languageChanged', function (lng) {
        moment.locale(momentLocale(lng));
        // Names in Latin in English (15 Oct 2026) — utils/Athlete/personName.js.
        setNameLanguage(lng);
    })
    .init({
        interpolation: {
            escapeValue: false, // React already does escaping
            format: function(value, format, lng) {
                if (format === 'uppercase') return value.toUpperCase();
                if (value instanceof Date) return moment(value).format(format);
                return value;
            }
        },
        resources: {
            en: {
                translation: translationEn,
            },
            ua: {
                translation: translationUa,
            },
        },
        fallbackLng: 'en',
        load: 'languageOnly',
        lng: onPublicPage ? publicLocale() : storedLocale(),
        // A public page writes nothing to the platform's key.
        detection: { caches: onPublicPage ? [] : ['localStorage'] },
        debug: parseInt(process.env.REACT_APP_DEBUGING),
    })
    .then(() => {
        moment.locale(momentLocale(i18n.language));
        setNameLanguage(i18n.language);
    })
;

export default i18n;